import { useState, useEffect, useCallback } from 'react';
import { Container } from '@/components/Container';
import { createStorage } from '@/lib/storage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAffiliate } from '@/hooks/useAffiliate';
import { ExternalLink } from 'lucide-react';
import { useAnalytics } from '@/hooks/useAnalytics';

/* ═══════════════════════════════════════════
   O QUE LEVAR — Checklist interativo
   Persiste no localStorage via factory
   ═══════════════════════════════════════════ */

const checklistStorage = createStorage<Record<string, boolean>>('checklist-guia', {});

const essenciaisIds = [
  'doc',
  'cash',
  'backpack',
  'repellent',
  'sunscreen',
  'water',
  'sneakers',
  'flipflops',
  'towel',
  'medicines',
  'casaco',
  'agasalho',
];

const recomendadosIds = [
  'powerbank',
  'flashlight',
  'sunglasses',
  'hat',
  'snorkel',
  'raincoat',
  'uvshirt',
  'ebook',
  'padlock',
  'shampoo',
];

const naoLevarIds = ['pet', 'car', 'bike', 'rollerbag', 'drone', 'fireworks'];

export function OQueLevarSection() {
  const { t } = useTranslation();
  const { generateLink } = useAffiliate();
  const { trackAffiliate } = useAnalytics();
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setChecked(checklistStorage.get());
    setMounted(true);
  }, []);

  const toggle = useCallback((id: string) => {
    setChecked(prev => {
      const next = { ...prev, [id]: !prev[id] };
      checklistStorage.set(next);
      return next;
    });
  }, []);

  const total = essenciaisIds.length + recomendadosIds.length;
  const done = Object.values(checked).filter(Boolean).length;
  const progress = mounted ? Math.round((done / total) * 100) : 0;

  const essenciaisDone = essenciaisIds.filter(i => checked[i]).length;
  const recomendadosDone = recomendadosIds.filter(i => checked[i]).length;

  return (
    <section id="o-que-levar" className="py-16 sm:py-20 bg-sand-50">
      <Container>
        {/* Header */}
        <div className="text-center mb-12">
          <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
            {t('guide.packing.overline')}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl text-warm-dark mt-2">
            {t('guide.packing.title')}
          </h2>
          <p className="font-editorial text-lg text-warm-gray mt-2 max-w-2xl mx-auto">
            {t('guide.packing.description')}
          </p>
        </div>

        {/* Progress bar */}
        <div className="max-w-xl mx-auto mb-12">
          <div className="flex items-center justify-between mb-2">
            <span className="font-body text-sm text-warm-gray">
              {t('guide.packing.progressLabel')
                .replace('{{done}}', String(done))
                .replace('{{total}}', String(total))}
            </span>
            <span className="font-body text-sm font-semibold text-teal-deep">{progress}%</span>
          </div>
          <div className="h-3 bg-sand-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-teal-deep rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Two-column checklist */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Essencial */}
          <div className="bg-white rounded-2xl border border-sand-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">🎒</span>
              <h3 className="font-display text-xl text-warm-dark">
                {t('guide.packing.essential')}
              </h3>
              <span className="ml-auto font-body text-xs text-coral font-semibold">
                {essenciaisDone}/{essenciaisIds.length}
              </span>
            </div>
            <div className="space-y-2">
              {essenciaisIds.map(id => (
                <label
                  key={id}
                  className={`flex items-start gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                    checked[id] ? 'bg-teal-50' : 'hover:bg-sand-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={!!checked[id]}
                    onChange={() => toggle(id)}
                    className="w-5 h-5 mt-0.5 rounded border-sand-300 text-teal-deep focus:ring-teal-400 flex-shrink-0"
                  />
                  <span
                    className={`font-body text-sm ${
                      checked[id] ? 'text-warm-gray line-through' : 'text-warm-dark'
                    }`}
                  >
                    {t(`guide.packing.items.${id}`)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Recomendado */}
          <div className="bg-white rounded-2xl border border-sand-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">✨</span>
              <h3 className="font-display text-xl text-warm-dark">
                {t('guide.packing.recommended')}
              </h3>
              <span className="ml-auto font-body text-xs text-coral font-semibold">
                {recomendadosDone}/{recomendadosIds.length}
              </span>
            </div>
            <div className="space-y-2">
              {recomendadosIds.map(id => (
                <label
                  key={id}
                  className={`flex items-start gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                    checked[id] ? 'bg-teal-50' : 'hover:bg-sand-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={!!checked[id]}
                    onChange={() => toggle(id)}
                    className="w-5 h-5 mt-0.5 rounded border-sand-300 text-teal-deep focus:ring-teal-400 flex-shrink-0"
                  />
                  <span
                    className={`font-body text-sm ${
                      checked[id] ? 'text-warm-gray line-through' : 'text-warm-dark'
                    }`}
                  >
                    {t(`guide.packing.items.${id}`)}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Alerta: o que NÃO levar */}
        <div className="mt-10 max-w-2xl mx-auto">
          <div className="bg-coral/5 border border-coral/20 rounded-xl p-5">
            <h4 className="font-display text-lg text-warm-dark mb-3 flex items-center gap-2">
              <span>🚫</span> {t('guide.packing.notToBring.title')}
            </h4>
            <div className="grid sm:grid-cols-2 gap-3">
              {naoLevarIds.map(id => (
                <div key={id} className="flex items-start gap-2">
                  <span className="text-coral font-bold mt-0.5">×</span>
                  <span className="font-body text-sm text-warm-gray">
                    {t(`guide.packing.notToBring.${id}`)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Card afiliado Seguros Promo */}
        <div className="mt-10 max-w-2xl mx-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <span className="text-2xl">🛡️</span>
              <div>
                <h3 className="font-display text-xl text-warm-dark mb-1">
                  {t('guide.packing.affiliate.insurance.title', 'Não esqueça o seguro viagem')}
                </h3>
                <p className="font-body text-sm text-warm-gray">
                  {t(
                    'guide.packing.affiliate.insurance.subtitle',
                    'Compare planos e viaje com segurança. Comissão progressiva de 50-65%'
                  )}
                </p>
              </div>
            </div>
            <a
              href={generateLink('segurosPromo', 'checklist')}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={() =>
                trackAffiliate({
                  provider: 'Seguros Promo',
                  platform: 'outro',
                  productType: 'insurance',
                })
              }
              className="inline-flex items-center gap-2 bg-coral hover:bg-[#c96a52] text-white font-body font-semibold rounded-xl px-6 py-3 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-coral/30 uppercase tracking-wide text-sm"
            >
              {t('guide.packing.affiliate.insurance.cta', 'Compare seguros')}
              <ExternalLink className="w-4 h-4" />
            </a>
            <p className="font-body text-xs text-warm-gray mt-3">
              {t('guide.affiliateBadge', 'Link afiliado — você não paga mais por isso')}
            </p>
          </div>
        </div>
      </Container>
    </section>
  );
}
